import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  createProductManagerSession,
  streamProductManagerMessage,
} from "../api/services/productManagerAgentService";
import {
  clearStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import { AgentServiceError } from "../utils/agentErrors";
import type { AgentContextUsage } from "../types/agentChat";

export interface ProductManagerChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const DEFAULT_WELCOME =
  "Product Manager Catalog Assistant is ready. I can help you create and edit products, categories, APIs, FAQs, documents, links, developer guides, operational errors, and more. For App KYC, use the App KYC Hub.";

function welcomeMessages(content = DEFAULT_WELCOME): ProductManagerChatMessage[] {
  return [
    {
      id: "welcome",
      role: "assistant",
      content,
      timestamp: new Date(),
    },
  ];
}

export function useProductManagerAgentChat() {
  const { activeTenantId } = useAuth();
  const [messages, setMessages] = useState<ProductManagerChatMessage[]>(() =>
    welcomeMessages(),
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sessionInitializing, setSessionInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contextWindowExceeded, setContextWindowExceeded] = useState(false);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const tenantRef = useRef(activeTenantId);

  const createSession = useCallback(async (): Promise<string | null> => {
    if (!activeTenantId) {
      setSessionId(null);
      setSessionReady(false);
      return null;
    }

    setSessionInitializing(true);
    setError(null);
    setContextWindowExceeded(false);
    setContextUsage(null);
    try {
      const result = await createProductManagerSession();
      setSessionId(result.session_id);
      setStoredAgentSession("product-manager", activeTenantId, result.session_id);
      setMessages(welcomeMessages(result.response || DEFAULT_WELCOME));
      setSessionReady(true);
      return result.session_id;
    } catch (err: unknown) {
      setSessionId(null);
      setSessionReady(false);
      setError(
        err instanceof Error ? err.message : "Failed to start catalog assistant session",
      );
      return null;
    } finally {
      setSessionInitializing(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    if (tenantRef.current && tenantRef.current !== activeTenantId) {
      clearStoredAgentSession("product-manager", tenantRef.current);
      setSessionId(null);
      setSessionReady(false);
      setMessages(welcomeMessages());
      setContextWindowExceeded(false);
    setContextUsage(null);
    }
    tenantRef.current = activeTenantId;

    if (!activeTenantId) {
      setSessionInitializing(false);
      setSessionReady(false);
      setSessionId(null);
      return;
    }

    void createSession();
  }, [activeTenantId, createSession]);

  const startNewChat = useCallback(async () => {
    if (!activeTenantId || sessionInitializing || loading) return;
    clearStoredAgentSession("product-manager", activeTenantId);
    setSessionId(null);
    setMessages(welcomeMessages());
    setContextUsage(null);
    await createSession();
  }, [activeTenantId, createSession, sessionInitializing, loading]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading || sessionInitializing || !sessionReady || !sessionId) {
        return null;
      }

      setError(null);
      setContextWindowExceeded(false);
    setContextUsage(null);
      setLoading(true);
      const userMsg: ProductManagerChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };
      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: ProductManagerChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      try {
        const result = await streamProductManagerMessage(sessionId, trimmed, {
          onTextDelta: (delta) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + delta }
                  : m,
              ),
            );
          },
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: result.response }
              : m,
          ),
        );
        setContextUsage(result.context_usage ?? null);
        return result.response;
      } catch (err: unknown) {
        const isContextError =
          err instanceof AgentServiceError &&
          err.code === "context_window_exceeded";
        setContextWindowExceeded(isContextError);
        setError(
          err instanceof Error ? err.message : "Failed to send message",
        );
        setMessages((prev) => {
          const assistant = prev.find((m) => m.id === assistantId);
          if (assistant && !assistant.content.trim()) {
            return prev.filter((m) => m.id !== assistantId);
          }
          return prev;
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionInitializing, sessionReady, sessionId],
  );

  return {
    messages,
    loading,
    error,
    contextWindowExceeded,
    contextUsage,
    sessionReady,
    sessionInitializing,
    sessionId,
    sendMessage,
    startNewChat,
  };
}
